import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from '@akin-home/toast/native';
import { UseMutationOptions } from 'react-query';
import { RobotDevice } from '../../types';
import { RobotIdentity } from '../types';
import {
  useCreateRobotFaceEmbeddings,
  useDeleteRobotFaceEmbeddings,
  useUpdateRobotFaceEmbeddings,
} from './mutations';

export const useEditRobotFaceEmbeddings = (
  robot: RobotDevice | undefined,
  options?: {
    createOptions?: UseMutationOptions<RobotIdentity, unknown, string, unknown>;
    updateOptions?: UseMutationOptions<void, unknown, string, unknown>;
    deleteOptions?: UseMutationOptions<void, unknown, string, unknown>;
  }
) => {
  const createMutation = useCreateRobotFaceEmbeddings(
    robot,
    options?.createOptions
  );
  const updateMutation = useUpdateRobotFaceEmbeddings(
    robot,
    options?.updateOptions
  );
  const deleteMutation = useDeleteRobotFaceEmbeddings(
    robot,
    options?.deleteOptions
  );

  const onCreateFaceEmbeddings = async (name: string) => {
    if (createMutation.isLoading) {
      return;
    }

    showInfoToast({
      text1: 'Learning Face...',
      text2: 'Please look at your robot',
    });

    try {
      const new_identity = await createMutation.mutateAsync(name);
      showSuccessToast({
        text1: 'Face Learnt',
      });
      return new_identity.uuid;
    } catch (err) {
      console.error('Error when updating robot face embeddings:', err);
      showErrorToast({
        text1: 'Error in Learning Face',
        text2: 'Please try again.',
      });
    }
  };

  const onChangeFaceEmbeddings = async (identity_id: string) => {
    if (updateMutation.isLoading || identity_id === undefined) {
      return;
    }

    showInfoToast({
      text1: 'Learning Face...',
      text2: 'Please look at your robot',
    });

    try {
      await updateMutation.mutateAsync(identity_id);
      showSuccessToast({
        text1: 'Face Learnt',
      });
    } catch (err) {
      console.error('Error when updating robot face embeddings:', err);
      showErrorToast({
        text1: 'Error in Learning Face',
        text2: 'Please try again.',
      });
    }
  };

  const onDeleteFaceEmbeddings = async (identity_id: string) => {
    if (deleteMutation.isLoading || identity_id === undefined) {
      return;
    }

    showInfoToast({ text1: 'Deleting Face...' });

    try {
      await deleteMutation.mutateAsync(identity_id);
      showSuccessToast({
        text1: 'Face Deleted',
      });
    } catch (err) {
      console.error('Error when deleting robot face embeddings:', err);
      showErrorToast({
        text1: 'Error in Deleting Face',
        text2: 'Please try again.',
      });
    }
  };

  return {
    onCreateFaceEmbeddings,
    onChangeFaceEmbeddings,
    onDeleteFaceEmbeddings,
    isLoading:
      createMutation.isLoading ||
      updateMutation.isLoading ||
      deleteMutation.isLoading,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
